import { useCallback, useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import Sidebar from './components/Sidebar'
import Toaster from './components/Toaster'
import Login from './pages/Login'
import LedgerCollector from './pages/LedgerCollector'
import LedgerReconciler from './pages/LedgerReconciler'
import AccountantRemediation from './pages/AccountantRemediation'
import MyLedgers from './pages/MyLedgers'
import MyLedgerDetail from './pages/MyLedgerDetail'
import Reports from './pages/Reports'
import AccountsTeam from './pages/AccountsTeam'
import { adoptHandoffToken, restoreSession, signOut, NO_ACCESS } from './lib/auth'
import * as api from './lib/api'
import { AccessContext } from './lib/access'

/**
 * Shell, sign-in and theme.
 *
 * Sign-in state is one value with three states rather than two independent
 * booleans, so logging back in can never land on a stale confirm dialog.
 */
export default function App() {
  /**
   * `checking` is the state this screen used not to have, and needed.
   *
   * A stored session has to be revalidated against Vision on load — an account
   * disabled this morning must stop working here today. That is a round trip,
   * and without a state for "we do not know yet" the login form flashes up in
   * front of somebody who is already signed in, every single refresh.
   */
  const [auth, setAuth] = useState('checking') // 'checking' | 'out' | 'confirming' | 'in'
  const [user, setUser] = useState(null)
  // Why the login form is showing, when it wasn't the person's own choice.
  const [loginNotice, setLoginNotice] = useState('')
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('lereco-theme') || 'dark'
    } catch {
      return 'dark'
    }
  })

  useEffect(() => {
    let cancelled = false
    // A token in the URL fragment first: somebody arriving from the Binny's One
    // launcher has already signed in, and a second login form is exactly what
    // that dashboard exists to avoid. Falls back to the stored session, which
    // is trusted outright — see restoreSession for why that is not lax.
    adoptHandoffToken()
      .then((handed) => handed ?? restoreSession())
      .then((signedIn) => {
        if (cancelled) return
        setUser(signedIn ?? null)
        setAuth(signedIn ? 'in' : 'out')
      })
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * Which side of lereco this person gets — `{ isAdmin, accountant }`, from
   * lereco's own `/me` (Vision's session knows nothing about it). `null`
   * until asked. A failed ask falls back to the non-admin side rather than
   * guessing upward: the server refuses every admin API to a non-admin
   * anyway, so the worst a wrong guess here could do is show fewer pages.
   */
  const [access, setAccess] = useState(null)
  const signedIn = auth === 'in' || auth === 'confirming'

  useEffect(() => {
    if (!signedIn || access) return
    let cancelled = false
    api
      .getMe()
      .then((me) => !cancelled && setAccess(me))
      .catch((e) => {
        if (cancelled) return
        // A stored session for someone who isn't an admin or on the accounts
        // team (removed since they last signed in): sign them straight out.
        if (e?.code === 'NOT_A_LERECO_USER') {
          signOut()
          setUser(null)
          setLoginNotice(NO_ACCESS)
          setAuth('out')
          return
        }
        setAccess({ isAdmin: false, accountant: null })
      })
    return () => {
      cancelled = true
    }
  }, [signedIn, access])

  const logOut = useCallback(() => {
    signOut()
    setUser(null)
    setAccess(null)
    setAuth('out')
  }, [])

  /*
   * A 401 from any API call means the session itself died — an expired token,
   * or an account disabled while somebody had the screen open. Without this,
   * every panel shows "Not authenticated" with no way back to the login form
   * short of a refresh.
   *
   * Deliberately not 403: that means one request was not permitted, not that
   * the session is bad, and logging out on it is how PCC used to eject every
   * non-admin the moment a screen touched an admin-only endpoint.
   */
  useEffect(() => {
    window.addEventListener('lereco:unauthorized', logOut)
    return () => window.removeEventListener('lereco:unauthorized', logOut)
  }, [logOut])

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    try {
      localStorage.setItem('lereco-theme', theme)
    } catch {
      /* private browsing — the theme just won't persist */
    }
  }, [theme])

  // Deliberately blank rather than a spinner: the check is one request against a
  // warm session, and a flash of "Loading…" on every refresh is more noticeable
  // than a frame of nothing.
  if (auth === 'checking') return <div className="app" />

  if (auth === 'out') {
    return (
      <Login
        notice={loginNotice}
        onSignIn={(signedIn, me) => {
          setLoginNotice('')
          setUser(signedIn ?? null)
          setAccess(me ?? null)
          setAuth('in')
        }}
      />
    )
  }

  // Same blank-frame reasoning as `checking` above — one quick request, and
  // rendering the admin nav for a split second before it disappears would be
  // worse than a frame of nothing.
  if (!access) return <div className="app" />


  return (
    <>
      <div className="app">
        <Sidebar
          user={user}
          access={access}
          theme={theme}
          onToggleTheme={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
          onLogout={() => setAuth('confirming')}
        />

        <main className="main">
          <AccessContext.Provider value={access}>
            {access.isAdmin ? (
              <Routes>
                <Route path="/" element={<Navigate to="/ledger-collector" replace />} />
                <Route path="/ledger-collector" element={<LedgerCollector />} />
                <Route path="/ledger-reconciler" element={<LedgerReconciler />} />
                <Route path="/remediation" element={<AccountantRemediation />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/team" element={<AccountsTeam />} />
                {/* An admin who is also an accountant gets their own work list too. */}
                {access.accountant && <Route path="/my-ledgers" element={<MyLedgers access={access} />} />}
                {access.accountant && <Route path="/my-ledgers/:id" element={<MyLedgerDetail />} />}
                <Route path="*" element={<Navigate to="/ledger-collector" replace />} />
              </Routes>
            ) : (
              // Accountants: the same screens to look at (no Reports, no Accounts
              // Team), view-only — plus their own work on My Ledgers.
              <Routes>
                <Route path="/ledger-collector" element={<LedgerCollector />} />
                <Route path="/ledger-reconciler" element={<LedgerReconciler />} />
                <Route path="/remediation" element={<AccountantRemediation />} />
                <Route path="/my-ledgers" element={<MyLedgers access={access} />} />
                <Route path="/my-ledgers/:id" element={<MyLedgerDetail />} />
                <Route path="*" element={<Navigate to="/my-ledgers" replace />} />
              </Routes>
            )}
          </AccessContext.Provider>
        </main>
      </div>
      <Toaster />

      {auth === 'confirming' && (
        <div className="cmbk" onClick={() => setAuth('in')}>
          <div className="cmodal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="cmtitle">Log out?</div>
            <div className="cmmsg">Statements already collected stay collected.</div>
            <div className="cmacts">
              <button className="btn" onClick={() => setAuth('in')}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                // Cleared here first; Vision is told after and is not waited
                // on. A logout that can fail is a logout that can trap somebody
                // signed in, which is the one outcome it must never have.
                onClick={logOut}
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
